import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StickyNote, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "@/lib/format";
import { downloadCSV, matchesSearch } from "@/lib/csvExport";

export default function AdminCustomersTab({ customers, setCustomers }) {
  const [customerSearch, setCustomerSearch] = useState("");
  const [notesCustomer, setNotesCustomer] = useState(null);
  const [notesText, setNotesText] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const filteredCustomers = customers.filter((c) =>
    matchesSearch([c.customer_id, c.full_name, c.email, c.phone], customerSearch)
  );

  const openNotes = (c) => { setNotesCustomer(c); setNotesText(c.notes || ""); };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/admin/customers/${notesCustomer.customer_id}/notes`, { notes: notesText });
      setCustomers((prev) => prev.map((c) => c.customer_id === data.customer_id ? { ...c, notes: data.notes } : c));
      toast.success("Notes saved");
      setNotesCustomer(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSavingNotes(false); }
  };

  const exportCustomersCSV = () => {
    if (filteredCustomers.length === 0) {
      toast.error("No customers to export");
      return;
    }
    const headers = ["Customer ID", "Name", "Email", "Phone", "Addresses", "Orders", "Repairs", "Total Spent (IDR)", "Last Order Date", "Notes"];
    const rows = filteredCustomers.map((c) => [
      c.customer_id,
      c.full_name || "",
      c.email || "",
      c.phone || "",
      (c.addresses || []).length,
      c.order_count,
      c.repair_count,
      c.total_spent,
      c.last_order_at ? c.last_order_at.slice(0, 10) : "",
      c.notes || "",
    ]);
    downloadCSV(headers, rows, "mobile-xpressed-customers");
    toast.success(`Exported ${filteredCustomers.length} customer(s)`);
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[240px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            data-testid="customer-search-input"
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            placeholder="Search by name, phone, or email…"
            className="rounded-full bg-white border-zinc-200 h-11 pl-10"
          />
        </div>
        <Button
          data-testid="export-customers-csv-btn"
          onClick={exportCustomersCSV}
          variant="outline"
          className="rounded-full border-2 border-zinc-200 hover:border-zinc-900 bg-white text-zinc-900 h-11 px-5"
        >
          <Download className="w-4 h-4 mr-1.5" /> Export CSV {customerSearch.trim() && `(${filteredCustomers.length})`}
        </Button>
      </div>
      <div className="bg-white rounded-3xl border border-zinc-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Addresses</TableHead>
              <TableHead>Orders</TableHead>
              <TableHead>Repairs</TableHead>
              <TableHead>Total Spent</TableHead>
              <TableHead>Last Order</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-zinc-500 py-10">No customers yet</TableCell></TableRow>
            )}
            {customers.length > 0 && filteredCustomers.length === 0 && (
              <TableRow><TableCell data-testid="customer-search-empty" colSpan={8} className="text-center text-sm text-zinc-500 py-10">No customers match "{customerSearch}"</TableCell></TableRow>
            )}
            {filteredCustomers.map((c) => (
              <TableRow key={c.customer_id} data-testid={`admin-customer-row-${c.customer_id}`}>
                <TableCell>
                  <div className="font-medium text-zinc-900">{c.full_name}</div>
                  <div className="text-xs text-zinc-500">{c.email}</div>
                </TableCell>
                <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                <TableCell className="text-sm">{(c.addresses || []).length}</TableCell>
                <TableCell className="text-sm">{c.order_count}</TableCell>
                <TableCell className="text-sm">{c.repair_count}</TableCell>
                <TableCell className="text-sm font-semibold">{formatIDR(c.total_spent)}</TableCell>
                <TableCell className="text-sm text-zinc-500">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  <Button
                    data-testid={`customer-notes-btn-${c.customer_id}`}
                    onClick={() => openNotes(c)}
                    variant="outline"
                    size="sm"
                    className={`rounded-full border-zinc-200 ${c.notes ? "text-brand border-brand/30 bg-red-50/50" : "text-zinc-600"}`}
                  >
                    <StickyNote className="w-3.5 h-3.5 mr-1.5" /> {c.notes ? "Edit note" : "Add note"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!notesCustomer} onOpenChange={(v) => !v && setNotesCustomer(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Customer notes</DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Private CRM notes for {notesCustomer?.full_name} — only visible to admins, never shown to the customer.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="customer-notes-textarea"
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            rows={6}
            placeholder="e.g. Prefers evening calls, sensitive about repair delays, VIP customer..."
            className="rounded-xl bg-zinc-50 border-zinc-200"
          />
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setNotesCustomer(null)}>Cancel</Button>
            <Button data-testid="save-customer-notes-btn" onClick={saveNotes} disabled={savingNotes} className="rounded-full bg-brand hover:bg-brand-hover text-white">
              {savingNotes ? "Saving…" : "Save notes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
